"use client";
import "./careScreenBanner.scss";
import Image from "next/image";
import { countryData } from "@/utils/data";
import { Phone } from 'lucide-react';
import Marquee from "react-fast-marquee";
import { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";
import {
  searchTopDoctors,
  searchTopHospitals,
  searchTreatments,
} from "@/slice/searchSlice";
import { debounce } from "@/utils/debounce";
import Link from "next/link";
import { routeName } from "@/utils/commonfuntions";

export default function CareScreenBanner() {
  const [modal, setModal] = useState(false);
  const dispatch = useDispatch();

  const doctors = useSelector((state) => state.search.doctors);
  const hospitals = useSelector((state) => state.search.hospitals);
  const treatments = useSelector((state) => state.search.treatments);

  // Fix the debounced search implementation
  const handleSearch = useRef(
    debounce((query) => {
      if (query) {
        dispatch(searchTopDoctors(query));
        dispatch(searchTopHospitals(query));
        dispatch(searchTreatments(query));
      }
    }, 500)
  ).current;

  const handleInputChange = (event) => {
    const query = event.target.value;
    handleSearch(query);
  };

  const intl = useIntl();
  const locale = intl.locale || "en";

  const modalRef = useRef(null);

  const handleClickOutside = (event) => {
    if (modalRef.current && !modalRef.current.contains(event.target)) {
      setModal(false);
    }
  };

  useEffect(() => {
    if (modal) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [modal]);
  return (
    <>
      {modal && (
        <div
          className="fixed inset-0 bg-black/30 z-[1100]"
          aria-hidden="true"
        />
      )}
      <div className="careBannerWrapper relative" data-locale={locale}>
        <div className="careBannerInfoContainer">
          <div className="careBannerInfo mb-4">
            <div className="heading3">
              <h4 className="">{intl.formatMessage({ id: "careScreenBanner.whenSomeoneIn", defaultMessage: "When someone in" })}</h4>
              <span className="heading4">
                <h1 className={`font-roboto-slab ${locale === "en" ? "noWrap" : ""}`}>
                  <span className="text-[#454F66] font-extralight">{intl.formatMessage({ id: "careScreenBanner.familyIs", defaultMessage: "Family is" })}</span> {intl.formatMessage({ id: "careScreenBanner.criticallyIll", defaultMessage: "Critically ill," })}</h1>
              </span>
              <span className="heading4">
                <h1 className="font-roboto-slab">{intl.formatMessage({ id: "careScreenBanner.fearIsReal", defaultMessage: "Fear is real" })}</h1>
              </span>
            </div>
          </div>

          <div className="careBannerSearchWrapper bubbleImage">
            <Image
              src="/images/Union.svg"
              alt={intl.formatMessage({ id: "careScreenBanner.bubbleImageAlt", defaultMessage: "doctor info bubble" })}
              width={600}
              height={600}
            />
            <div className="bubbleText">
              <p className="line1">{intl.formatMessage({ id: "careScreenBanner.bubbleLine1", defaultMessage: "You don't have to figure this out alone" })}</p>
              <p className="line2">{intl.formatMessage({ id: "careScreenBanner.bubbleLine2", defaultMessage: "Fitsib helps you understand options and decide calmly" })}</p>
            </div>
          </div>
          {/* Mobile-only plain text (keeps decorative bubble for desktop/tablet) */}
          <div className="bubbleTextMobile" aria-hidden={false}>
            <p className="line1">{intl.formatMessage({ id: "careScreenBanner.bubbleLine1", defaultMessage: "You don't have to figure this out alone" })}</p>
            <p className="line2">{intl.formatMessage({ id: "careScreenBanner.bubbleLine2", defaultMessage: "Fitsib helps you understand options and decide calmly" })}</p>
          </div>

          {/* CTA Buttons */}
          <div className="ctaButtonsContainer lg:justify-start justify-center pl-2">
            <a
              href="https://api.whatsapp.com/send?phone=919211367800&text=Hello,%20please%20contact%20me%20regarding%20treatment%20options,%20Thank%20you!"
              target="_blank"
              rel="noopener noreferrer"
            >
              <button className="primaryBtn">
                <span className="font-roboto text-[15px] font-medium tracking-wide">{intl.formatMessage({ id: "careScreenBanner.getImmediateHelp", defaultMessage: "Get Immediate Help" })}</span>
                <Image
                  src="/images/Whatsapp-logo.svg"
                  alt={intl.formatMessage({ id: "careScreenBanner.whatsappAlt", defaultMessage: "WhatsApp" })}
                  width={30}
                  height={30}
                  className="btnIcon"
                />
              </button>
            </a>
            <a
              href="tel:+917303334800"
              target="_blank"
              rel="noopener noreferrer"
            >
              <button className="secondaryBtn">
                <Phone className="btnIcon" size={20} />
                <span className="font-roboto text-[15px] font-medium tracking-wide">+91-730-333-4800</span>
              </button>
            </a>
          </div>

          <div className="careBannerFooter lg:pl-[15px] pl-3 mt-3 text-center leading-6 md:text-left">
            <p className="font-roboto lg:text-[14px] text-[12px] text-[#5A6B85] tracking-regular italic">
              {intl.formatMessage({ id: "careScreenBanner.footerText", defaultMessage: "Difficult moments deserve a human, not a system. Real conversations start here" })}
            </p>
            {/* <span className="font-roboto lg:text-[16px] text-[13px] font-regular italic lg:mt-3 text-[#454F66] tracking-wide pl-0">conversations start here</span> */}
          </div>
        </div>

        {/* Hero Image */}
        <div className="careBannerBg">
          <Image
            src="/images/Hero_Banner_Doctor.webp"
            alt={intl.formatMessage({ id: "careScreenBanner.bannerImageAlt", defaultMessage: "care screen banner background" })}
            fill
            priority
            className="heroImage heroImageDesktop"
          />
          <Image
            src="/images/Mobile_Hero.svg"
            alt={intl.formatMessage({ id: "careScreenBanner.bannerImageAltMobile", defaultMessage: "care screen mobile banner background" })}
            fill
            className="heroImage heroImageMobile"
          />
        </div>
      </div>
      {/* Countries Banner  */}
      <div className="countriesBannerSection">
        <Marquee className="countriesContainer" speed={30} autoFill={true}>
          {countryData.map((country, index) => (
            <div key={index} className="countryInfo">
              <Image src={`/images/${country.name.toLowerCase()}.svg`} alt={country.name} width={30} height={30} />
              <span
                className={`countryName ${country.slug ? country.slug : country.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {country.name}
              </span>
            </div>
          ))}
        </Marquee>
      </div>
    </>
  );
}
